import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Clock, LogIn, LogOut, CheckCircle, Users, UserX, CalendarDays, Wrench, Loader2, Home } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AttendancePage = () => {
  const { user, isHR } = useAuth();
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Admin team view
  const [teamData, setTeamData] = useState(null);
  const [teamDate, setTeamDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [teamLoading, setTeamLoading] = useState(false);

  // Regularisation
  const [regQuota, setRegQuota] = useState({ quota: 5, used: 0, remaining: 5 });
  const [regOpen, setRegOpen] = useState(false);
  const [regRecord, setRegRecord] = useState(null);
  const [regMode, setRegMode] = useState('checkout'); // 'checkout' | 'missed_day'
  const [regDate, setRegDate] = useState('');
  const [regCheckIn, setRegCheckIn] = useState('');
  const [regTime, setRegTime] = useState('');
  const [regReason, setRegReason] = useState('Missed check-out');
  const [regWfh, setRegWfh] = useState(false);
  const [regSubmitting, setRegSubmitting] = useState(false);

  // Daily status calendar (present / absent / on_leave / regularised / holiday …)
  const [calMonth, setCalMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [calendarDays, setCalendarDays] = useState([]);

  // WFH policy (global, HR-controlled) + this employee's monthly usage
  const [wfhPolicy, setWfhPolicy] = useState({ enabled: true, monthly_limit: 8, used: 0, remaining: 8 });
  const [wfhDraft, setWfhDraft] = useState({ enabled: true, monthly_limit: 8 });
  const [wfhSaving, setWfhSaving] = useState(false);

  const fetchWfhPolicy = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/attendance/wfh-policy`, { withCredentials: true });
      setWfhPolicy(res.data);
      setWfhDraft({ enabled: res.data.enabled, monthly_limit: res.data.monthly_limit });
    } catch { /* silent */ }
  }, []);

  const saveWfhPolicy = async () => {
    setWfhSaving(true);
    try {
      await axios.put(`${API_URL}/api/attendance/wfh-policy`, { enabled: wfhDraft.enabled, monthly_limit: Number(wfhDraft.monthly_limit) }, { withCredentials: true });
      toast.success('WFH policy updated');
      fetchWfhPolicy();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update WFH policy');
    } finally { setWfhSaving(false); }
  };

  const fetchCalendar = useCallback(async (month) => {
    try {
      const res = await axios.get(`${API_URL}/api/attendance/my-calendar?month=${month}`, { withCredentials: true });
      setCalendarDays(res.data.days || []);
    } catch { /* silent */ }
  }, []);

  const fetchRegQuota = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/attendance/regularise/quota`, { withCredentials: true });
      setRegQuota(res.data);
    } catch { /* silent */ }
  }, []);

  const openRegularise = (record) => {
    setRegMode('checkout');
    setRegRecord(record);
    setRegDate(record.date);
    setRegCheckIn('');
    setRegTime('18:00');
    setRegReason('Missed check-out');
    setRegWfh(false);
    setRegOpen(true);
  };

  const openRegulariseMissedDay = (presetDate) => {
    setRegMode('missed_day');
    setRegRecord(null);
    if (presetDate) {
      setRegDate(presetDate);
    } else {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      setRegDate(format(y, 'yyyy-MM-dd'));
    }
    setRegCheckIn('10:00');
    setRegTime('18:00');
    setRegReason('Missed check-in');
    setRegWfh(false);
    setRegOpen(true);
  };

  const submitRegularise = async () => {
    setRegSubmitting(true);
    try {
      const payload = { date: regDate, reason: regReason };
      if (regMode === 'checkout') {
        payload.check_out_time = regTime;
      } else {
        payload.check_in_time = regCheckIn;
        if (regTime) payload.check_out_time = regTime;
      }
      if (regWfh) payload.work_mode = 'wfh';
      await axios.post(`${API_URL}/api/attendance/regularise`, payload, { withCredentials: true });
      toast.success('Attendance regularised');
      setRegOpen(false);
      setRegRecord(null);
      fetchRegQuota();
      fetchCalendar(calMonth);
      fetchWfhPolicy();
      const response = await axios.get(`${API_URL}/api/attendance?employee_id=${user.id}`, { withCredentials: true });
      setAttendance(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Regularisation failed');
    } finally { setRegSubmitting(false); }
  };

  const fetchAttendance = useCallback(async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const response = await axios.get(`${API_URL}/api/attendance?employee_id=${user.id}`, { withCredentials: true });
      setAttendance(response.data);
      const todayRecord = response.data.find(a => a.date === today);
      setTodayAttendance(todayRecord);
    } catch (error) {
      console.error('Fetch attendance error:', error);
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchTeamAttendance = useCallback(async (date) => {
    setTeamLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/attendance/team?date=${date}`, { withCredentials: true });
      setTeamData(response.data);
    } catch (error) {
      console.error('Fetch team attendance error:', error);
    } finally {
      setTeamLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAttendance();
    fetchRegQuota();
    fetchCalendar(calMonth);
    fetchWfhPolicy();
    if (isHR) fetchTeamAttendance(teamDate);
  }, [fetchAttendance, fetchRegQuota, fetchCalendar, fetchWfhPolicy, calMonth, fetchTeamAttendance, isHR, teamDate]);

  const handleCheckIn = async () => {
    try {
      await axios.post(`${API_URL}/api/attendance/check-in`, {}, { withCredentials: true });
      toast.success('Checked in successfully!');
      fetchAttendance();
      if (isHR) fetchTeamAttendance(teamDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in');
    }
  };

  const handleCheckOut = async () => {
    try {
      await axios.post(`${API_URL}/api/attendance/check-out`, {}, { withCredentials: true });
      toast.success('Checked out successfully!');
      fetchAttendance();
      if (isHR) fetchTeamAttendance(teamDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check out');
    }
  };

  const handleMarkWFH = async () => {
    try {
      await axios.post(`${API_URL}/api/attendance/wfh`, {}, { withCredentials: true });
      toast.success('Marked as Work From Home for today');
      fetchAttendance();
      fetchCalendar(calMonth);
      fetchWfhPolicy();
      if (isHR) fetchTeamAttendance(teamDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark WFH');
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
  };

  const getStatusBadge = (status) => {
    const config = {
      present: { cls: 'badge-success', label: 'Present' },
      regularised: { cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'Regularised' },
      absent: { cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Absent' },
      on_leave: { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', label: 'On Leave' },
      holiday: { cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400', label: 'Holiday' },
      not_marked: { cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', label: 'Not marked' },
      upcoming: { cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400', label: 'Upcoming' },
      half_day: { cls: 'badge-warning', label: 'Half Day' },
      wfh: { cls: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400', label: 'WFH' },
    };
    const c = config[status] || { cls: 'badge-warning', label: status };
    return <Badge className={c.cls}>{c.label}</Badge>;
  };

  // date -> daily status entry, for calendar colouring + working-day table
  const dayEntryMap = calendarDays.reduce((acc, d) => { acc[d.date] = d; return acc; }, {});

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="attendance-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Clock className="w-7 h-7 text-primary" /> Attendance</h1>
        <p className="text-muted-foreground">{isHR ? 'Track team attendance, check-ins, and working hours' : 'Track your daily attendance and working hours'}</p>
      </div>

      {isHR && (
        <div className="rounded-lg border border-dashed p-4 bg-muted/30" data-testid="wfh-policy-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h4 className="font-medium text-sm flex items-center gap-2"><Home className="w-4 h-4" /> Work From Home Policy</h4>
              <p className="text-xs text-muted-foreground mt-0.5">Company-wide. Controls the self-service "Mark WFH" option for all employees.</p>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={wfhDraft.enabled} onChange={e => setWfhDraft({ ...wfhDraft, enabled: e.target.checked })} className="h-4 w-4 rounded border-input" data-testid="wfh-policy-enabled" />
                WFH enabled
              </label>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Max days / month</span>
                <Input type="number" min="0" max="31" value={wfhDraft.monthly_limit} onChange={e => setWfhDraft({ ...wfhDraft, monthly_limit: e.target.value })} className="h-8 w-20" disabled={!wfhDraft.enabled} data-testid="wfh-policy-limit" />
              </div>
              <Button size="sm" onClick={saveWfhPolicy} disabled={wfhSaving} className="gap-1.5" data-testid="wfh-policy-save">
                {wfhSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save Policy
              </Button>
            </div>
          </div>
        </div>
      )}

      {isHR ? (
        <Tabs defaultValue="team">
          <TabsList>
            <TabsTrigger value="team" className="gap-2" data-testid="tab-team"><Users className="w-4 h-4" /> Team Attendance</TabsTrigger>
            <TabsTrigger value="mine" className="gap-2" data-testid="tab-mine"><Clock className="w-4 h-4" /> My Attendance</TabsTrigger>
          </TabsList>
          <TabsContent value="team" className="mt-4">
            {renderTeamView()}
          </TabsContent>
          <TabsContent value="mine" className="mt-4">
            {renderMyAttendance()}
          </TabsContent>
        </Tabs>
      ) : renderMyAttendance()}
    </div>
  );

  function renderTeamView() {
    return (
      <div className="space-y-4">
        {/* Date picker + summary */}
        <div className="flex items-end gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Date</label>
            <Input type="date" value={teamDate} onChange={(e) => { setTeamDate(e.target.value); fetchTeamAttendance(e.target.value); }} className="w-48" data-testid="team-date-input" />
          </div>
          {teamData?.summary && (
            <div className="flex gap-3">
              <Badge className="badge-success gap-1"><CheckCircle className="w-3 h-3" /> {teamData.summary.present} Present</Badge>
              <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 gap-1"><UserX className="w-3 h-3" /> {teamData.summary.absent} Absent</Badge>
              <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 gap-1"><CalendarDays className="w-3 h-3" /> {teamData.summary.on_leave} On Leave</Badge>
            </div>
          )}
        </div>

        {/* Team table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Team Attendance — {teamDate}</CardTitle>
            <CardDescription>{teamData?.summary?.total || 0} employees</CardDescription>
          </CardHeader>
          <CardContent>
            {teamLoading ? (
              <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
            ) : teamData?.team?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Department</th>
                      <th>Check In</th>
                      <th>Check Out</th>
                      <th>Hours Worked</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamData.team.map((t) => (
                      <tr key={t.employee_id} data-testid={`team-row-${t.employee_id}`}>
                        <td className="font-medium">{t.name}</td>
                        <td className="text-sm text-muted-foreground">{t.department}</td>
                        <td className="font-mono">{formatTime(t.check_in)}</td>
                        <td className="font-mono">{formatTime(t.check_out)}</td>
                        <td className="font-mono">{t.worked_hours ? `${t.worked_hours}h` : '-'}</td>
                        <td>
                          {getStatusBadge(t.status)}
                          {t.leave_type && <span className="text-xs text-muted-foreground ml-1">({t.leave_type})</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-center text-muted-foreground py-8">No attendance data for this date</p>}
          </CardContent>
        </Card>
      </div>
    );
  }

  function renderMyAttendance() {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" /> Today's Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
                <div className="flex items-center gap-4 mt-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Check In</p>
                    <p className="font-mono text-lg">{formatTime(todayAttendance?.check_in)}</p>
                  </div>
                  <div className="w-px h-8 bg-border" />
                  <div>
                    <p className="text-xs text-muted-foreground">Check Out</p>
                    <p className="font-mono text-lg">{formatTime(todayAttendance?.check_out)}</p>
                  </div>
                  {todayAttendance && (
                    <>
                      <div className="w-px h-8 bg-border" />
                      <div>
                        <p className="text-xs text-muted-foreground">Status</p>
                        {getStatusBadge(todayAttendance.status)}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {!todayAttendance?.check_in ? (
                  <>
                    <Button onClick={handleCheckIn} className="gap-2" data-testid="check-in-btn"><LogIn className="w-4 h-4" /> Check In</Button>
                    {wfhPolicy.enabled ? (
                      <Button onClick={handleMarkWFH} variant="outline" className="gap-2" disabled={wfhPolicy.remaining <= 0} title={wfhPolicy.remaining <= 0 ? 'WFH limit reached this month' : ''} data-testid="mark-wfh-btn"><Home className="w-4 h-4" /> Mark WFH</Button>
                    ) : (
                      <Badge variant="secondary" className="gap-1 text-xs" data-testid="wfh-disabled-badge"><Home className="w-3 h-3" /> WFH disabled</Badge>
                    )}
                  </>
                ) : !todayAttendance?.check_out ? (
                  <>
                    <Button onClick={handleCheckOut} className="gap-2" variant="outline" data-testid="check-out-btn"><LogOut className="w-4 h-4" /> Check Out</Button>
                    {wfhPolicy.enabled && wfhPolicy.remaining > 0 && todayAttendance?.work_mode !== 'wfh' && (
                      <Button onClick={handleMarkWFH} variant="ghost" className="gap-2 text-xs" data-testid="mark-wfh-btn"><Home className="w-4 h-4" /> Switch to WFH</Button>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-[hsl(var(--success))]"><CheckCircle className="w-5 h-5" /><span className="text-sm font-medium">Day Complete{todayAttendance?.work_mode === 'wfh' ? ' · WFH' : ''}</span></div>
                )}
                {wfhPolicy.enabled && (
                  <span className="text-[11px] text-muted-foreground ml-1" data-testid="wfh-remaining">WFH: {wfhPolicy.used}/{wfhPolicy.monthly_limit} used this month</span>
                )}
              </div>
            </div>

            <div className="mt-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">Attendance — Working Days</h3>
                  <Input
                    type="month" value={calMonth} max={format(new Date(), 'yyyy-MM')}
                    onChange={(e) => setCalMonth(e.target.value)}
                    className="h-8 w-40 text-xs" data-testid="att-month-input"
                  />
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Wrench className="w-3.5 h-3.5" /> Regularisation quota:
                  <Badge variant="secondary" data-testid="reg-quota-badge">{regQuota.used}/{regQuota.quota} used · {regQuota.remaining} left</Badge>
                  {regQuota.remaining > 0 && (
                    <Button size="sm" variant="outline" onClick={() => openRegulariseMissedDay()} className="h-7 gap-1 text-xs ml-2" data-testid="reg-missed-day-btn">
                      <Wrench className="w-3 h-3" /> Regularise missed day
                    </Button>
                  )}
                </div>
              </div>
              {(() => {
                const today = format(new Date(), 'yyyy-MM-dd');
                const rows = calendarDays
                  .filter(d => d.is_working_day && d.date <= today)
                  .sort((a, b) => b.date.localeCompare(a.date));
                return (
                  <>
                    <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                      <table className="data-table">
                        <thead className="sticky top-0 bg-background z-10"><tr><th>Date</th><th>Check In</th><th>Check Out</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
                        <tbody>
                          {rows.map((d) => {
                            const missingOut = d.status === 'present' && !d.check_out;
                            const canRegCheckout = d.date < today && missingOut && regQuota.remaining > 0;
                            const canRegAbsent = d.status === 'absent' && regQuota.remaining > 0;
                            return (
                              <tr key={d.date} data-testid={`att-row-${d.date}`}>
                                <td>{new Date(d.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</td>
                                <td className="font-mono">{formatTime(d.check_in)}</td>
                                <td className="font-mono">
                                  {formatTime(d.check_out)}
                                  {d.regularised && <Badge variant="outline" className="ml-2 text-[10px]">Regularised</Badge>}
                                </td>
                                <td>
                                  {getStatusBadge(d.status)}
                                  {d.status === 'on_leave' && d.leave_type && <span className="text-xs text-muted-foreground ml-1">({d.leave_type}{d.half_day ? ' ½' : ''})</span>}
                                </td>
                                <td className="text-right">
                                  {canRegCheckout && (
                                    <Button size="sm" variant="outline" onClick={() => openRegularise({ date: d.date, check_in: d.check_in })} className="h-7 gap-1 text-xs" data-testid={`regularise-btn-${d.date}`}>
                                      <Wrench className="w-3 h-3" /> Regularise
                                    </Button>
                                  )}
                                  {canRegAbsent && (
                                    <Button size="sm" variant="outline" onClick={() => openRegulariseMissedDay(d.date)} className="h-7 gap-1 text-xs" data-testid={`regularise-absent-btn-${d.date}`}>
                                      <Wrench className="w-3 h-3" /> Mark attendance
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {rows.length === 0 && <p className="text-center text-muted-foreground py-4">No working days to show for this month yet</p>}
                    {rows.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">{rows.length} working day(s) this month · newest first</p>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Regularise dialog */}
            <Dialog open={regOpen} onOpenChange={setRegOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Wrench className="w-5 h-5" /> Regularise Attendance</DialogTitle>
                  <DialogDescription>
                    {regMode === 'checkout'
                      ? <>You forgot to check out on <b>{regRecord?.date}</b>. Fill in the actual out time — auto-applied without HR approval.</>
                      : <>You forgot to check in on a past day. Provide the date + your actual timings — auto-applied without HR approval.</>
                    }
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  {regMode === 'missed_day' && (
                    <div className="space-y-1">
                      <Label htmlFor="reg-date" className="text-xs">Date (past, within 60 days)</Label>
                      <Input id="reg-date" type="date" value={regDate} max={format(new Date(Date.now() - 24 * 3600 * 1000), 'yyyy-MM-dd')} onChange={e => setRegDate(e.target.value)} data-testid="reg-date-input" />
                    </div>
                  )}
                  {regMode === 'checkout' ? (
                    <div>
                      <Label className="text-xs">Check-in was at</Label>
                      <p className="font-mono text-sm">{formatTime(regRecord?.check_in)}</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <Label htmlFor="reg-checkin" className="text-xs">Check-in time (HH:MM)</Label>
                      <Input id="reg-checkin" type="time" value={regCheckIn} onChange={e => setRegCheckIn(e.target.value)} data-testid="reg-checkin-input" />
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label htmlFor="reg-time" className="text-xs">Check-out time (HH:MM){regMode === 'missed_day' ? ' — optional' : ''}</Label>
                    <Input id="reg-time" type="time" value={regTime} onChange={e => setRegTime(e.target.value)} data-testid="reg-time-input" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="reg-reason" className="text-xs">Reason (optional)</Label>
                    <Input id="reg-reason" value={regReason} onChange={e => setRegReason(e.target.value)} placeholder={regMode === 'checkout' ? 'Missed check-out' : 'Missed check-in'} data-testid="reg-reason-input" />
                  </div>
                  <label className={`flex items-center gap-2 text-xs select-none ${wfhPolicy.enabled ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`} data-testid="reg-wfh-toggle">
                    <input type="checkbox" checked={regWfh} disabled={!wfhPolicy.enabled} onChange={e => setRegWfh(e.target.checked)} className="h-4 w-4 rounded border-input" />
                    <Home className="w-3.5 h-3.5" /> This was a Work From Home day
                    {!wfhPolicy.enabled && <span className="text-muted-foreground">(WFH disabled)</span>}
                  </label>
                  <p className="text-[11px] text-muted-foreground">Quota this month: {regQuota.used}/{regQuota.quota} used. This will consume 1.</p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setRegOpen(false)}>Cancel</Button>
                  <Button
                    onClick={submitRegularise}
                    disabled={regSubmitting || (regMode === 'checkout' ? !regTime : !regDate || !regCheckIn)}
                    data-testid="reg-submit-btn"
                  >
                    {regSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Apply
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Attendance Calendar</CardTitle></CardHeader>
          <CardContent>
            <Calendar mode="single" selected={selectedDate} onSelect={(date) => date && setSelectedDate(date)} month={new Date(`${calMonth}-01T00:00:00`)} onMonthChange={(d) => setCalMonth(format(d, 'yyyy-MM'))} className="rounded-md border"
              modifiers={{
                present: (date) => dayEntryMap[format(date, 'yyyy-MM-dd')]?.status === 'present',
                wfh: (date) => dayEntryMap[format(date, 'yyyy-MM-dd')]?.status === 'wfh',
                regularised: (date) => dayEntryMap[format(date, 'yyyy-MM-dd')]?.status === 'regularised',
                on_leave: (date) => dayEntryMap[format(date, 'yyyy-MM-dd')]?.status === 'on_leave',
                absent: (date) => dayEntryMap[format(date, 'yyyy-MM-dd')]?.status === 'absent',
                holiday: (date) => dayEntryMap[format(date, 'yyyy-MM-dd')]?.status === 'holiday',
              }}
              modifiersClassNames={{
                present: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 font-semibold',
                wfh: 'bg-teal-500/20 text-teal-700 dark:text-teal-400 font-semibold',
                regularised: 'bg-blue-500/20 text-blue-700 dark:text-blue-400 font-semibold',
                on_leave: 'bg-amber-500/20 text-amber-700 dark:text-amber-400 font-semibold',
                absent: 'bg-red-500/20 text-red-700 dark:text-red-400 font-semibold',
                holiday: 'bg-violet-500/20 text-violet-700 dark:text-violet-400 font-semibold',
              }}
            />
            <div className="mt-4 flex flex-wrap gap-x-3 gap-y-2 text-xs">
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-emerald-500/40" /><span>Present</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-teal-500/40" /><span>WFH</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-blue-500/40" /><span>Regularised</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-amber-500/40" /><span>On Leave</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-red-500/40" /><span>Absent</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-violet-500/40" /><span>Holiday</span></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
};

export default AttendancePage;
